import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ShoppingBag, Video, Image, Package2, Home, Sparkles, Zap, Users, BarChart2, Link as LinkIcon, Share2, Rocket, LineChart, X, ArrowRight, MessageSquare, Plus, Settings, Check } from 'lucide-react';
import { GradientText } from '../components/ui/GradientText';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface DemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

function DemoModal({ isOpen, onClose, title }: DemoModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Animation effects
  useEffect(() => {
    if (isOpen && modalRef.current && contentRef.current) {
      // Animate the backdrop in
      modalRef.current.animate(
        [
          { opacity: 0 },
          { opacity: 1 }
        ],
        { duration: 300, easing: 'ease-out', fill: 'forwards' }
      );

      // Animate the content in
      contentRef.current.animate(
        [
          { opacity: 0, transform: 'scale(0.95) translateY(10px)' },
          { opacity: 1, transform: 'scale(1) translateY(0)' }
        ],
        { duration: 350, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
      );
    }

    // Add event listener to close on escape
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle close animation
  const handleClose = () => {
    if (modalRef.current && contentRef.current) {
      // Animate the content out
      const contentAnimation = contentRef.current.animate(
        [
          { opacity: 1, transform: 'scale(1) translateY(0)' },
          { opacity: 0, transform: 'scale(0.95) translateY(10px)' }
        ],
        { duration: 250, easing: 'cubic-bezier(0.7, 0, 0.84, 0)', fill: 'forwards' }
      );

      // Animate the backdrop out
      const backdropAnimation = modalRef.current.animate(
        [
          { opacity: 1 },
          { opacity: 0 }
        ],
        { duration: 200, easing: 'ease-in', fill: 'forwards' }
      );

      // Close the modal after animations complete
      backdropAnimation.onfinish = () => onClose();
    } else {
      onClose();
    }
  };

  // Handle add to chatbot
  const handleAddToChatbot = () => {
    if (user) {
      // If logged in, redirect directly to dashboard's mini apps settings
      navigate('/app/settings/mini-apps');
    } else {
      // If not logged in, redirect to pricing
      navigate('/pricing');
    }
    
    onClose();
  };

  if (!isOpen) return null;

  // Get icon component for the current app type
  const getIcon = () => {
    if (title === 'Product Catalog') {
      return <Package2 className="w-6 h-6" />;
    } else if (title === 'Appointment Scheduling') {
      return <Calendar className="w-6 h-6" />;
    } else if (title === 'Service Presentation') {
      return <ShoppingBag className="w-6 h-6" />;
    } else if (title === 'Video Gallery') {
      return <Video className="w-6 h-6" />;
    } else if (title === 'Photo Gallery') {
      return <Image className="w-6 h-6" />;
    } else if (title === 'Real Estate Properties') {
      return <Home className="w-6 h-6" />;
    } else if (title === 'Social Media Links') {
      return <Share2 className="w-6 h-6" />;
    } else if (title === 'Order Tracking') {
      return <MessageSquare className="w-6 h-6" />;
    } else {
      return <Package2 className="w-6 h-6" />;
    }
  };

  // Get description based on app type - using the exact same text as MiniAppDetails
  const getAppDescription = () => {
    if (title === 'Product Catalog') {
      return 'The Product Catalog mini app allows you to showcase your products directly in the chat interface. Users can browse through your catalog, view product details, and get information about pricing and availability without leaving the conversation.';
    } else if (title === 'Appointment Scheduling') {
      return 'The Appointment Scheduling mini app integrates with Calendly to allow users to book appointments directly from the chat. This seamless integration helps convert conversations into scheduled meetings with minimal friction.';
    } else if (title === 'Service Presentation') {
      return 'The Services Showcase mini app presents your services in an interactive carousel format. Each service can include a title, description, image, and call-to-action button to help users understand your offerings.';
    } else if (title === 'Video Gallery') {
      return 'The Video Gallery mini app allows you to share videos from YouTube, Vimeo, or your own sources directly in the chat interface. This creates an engaging multimedia experience for your users.';
    } else if (title === 'Photo Gallery') {
      return 'The Photo Gallery mini app displays your images in a beautiful, responsive grid layout. It\'s perfect for showcasing portfolios, products, events, or any visual content that enhances your conversation.';
    } else if (title === 'Real Estate Properties') {
      return 'The Real Estate Properties mini app is designed specifically for real estate businesses. It displays property listings with details like price, location, size, and features in an interactive interface.';
    } else if (title === 'Social Media Links') {
      return 'The Social Media Links mini app displays your social media profiles as interactive buttons. Users can easily connect with you on various platforms without leaving the chat.';
    } else if (title === 'Order Tracking') {
      return 'The Order Tracking feature allows customers to check the status of their orders by simply entering their order number. This integration works with your e-commerce platform to provide real-time order status updates.';
    } else {
      return 'This mini app enhances your chatbot with additional functionality.';
    }
  };

  // Get features based on app type - using the exact same features as MiniAppDetails
  const getAppFeatures = () => {
    if (title === 'Product Catalog') {
      return [
        'Interactive product catalog with images and descriptions',
        'Product categorization and filtering',
        'Price and availability information',
        'Integration with e-commerce platforms like Shopify and PrestaShop',
        'Customizable display options'
      ];
    } else if (title === 'Appointment Scheduling') {
      return [
        'Seamless Calendly integration',
        'Real-time availability display',
        'Automatic confirmation emails',
        'Calendar synchronization',
        'Customizable appointment types'
      ];
    } else if (title === 'Service Presentation') {
      return [
        'Interactive service carousel',
        'Rich media presentation with images',
        'Customizable call-to-action buttons',
        'Detailed service descriptions',
        'Organized service categories'
      ];
    } else if (title === 'Video Gallery') {
      return [
        'Support for YouTube, Vimeo, and custom videos',
        'Responsive video player',
        'Video thumbnails and previews',
        'Customizable display layouts',
        'Video categorization'
      ];
    } else if (title === 'Photo Gallery') {
      return [
        'Responsive image grid or carousel',
        'Lightbox image viewing',
        'Image categorization',
        'High-resolution image support',
        'Customizable display options'
      ];
    } else if (title === 'Real Estate Properties') {
      return [
        'Detailed property listings',
        'Property search and filtering',
        'Interactive image galleries',
        'Property specifications display',
        'Location information and maps'
      ];
    } else if (title === 'Social Media Links') {
      return [
        'Customizable social media buttons',
        'Support for all major social platforms',
        'Compact or expanded display options',
        'Direct links to your profiles',
        'Visual branding with platform colors and icons'
      ];
    } else if (title === 'Order Tracking') {
      return [
        'Real-time order status updates',
        'Integration with e-commerce platforms',
        'Customizable response templates',
        'Order history access',
        'Shipping and delivery information'
      ];
    } else {
      return [
        'Seamless integration with your chatbot',
        'Customizable appearance and behavior',
        'Enhanced user experience',
        'Increased engagement',
        'Improved conversion rates'
      ];
    }
  };

  // Get app image based on type - using the exact same images as MiniAppDetails
  const getAppImage = () => {
    if (title === 'Product Catalog') {
      return 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=800&h=600&q=80';
    } else if (title === 'Appointment Scheduling') {
      return 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=800&h=600&q=80';
    } else if (title === 'Service Presentation') {
      return 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&h=600&q=80';
    } else if (title === 'Video Gallery') {
      return 'https://images.unsplash.com/photo-1536240478700-b869070f9279?auto=format&fit=crop&w=800&h=600&q=80';
    } else if (title === 'Photo Gallery') {
      return 'https://images.unsplash.com/photo-1482245294234-b3f2f8d5f1a4?auto=format&fit=crop&w=800&h=600&q=80';
    } else if (title === 'Real Estate Properties') {
      return 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&h=600&q=80';
    } else if (title === 'Social Media Links') {
      return 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=800&h=600&q=80';
    } else if (title === 'Order Tracking') {
      return 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?auto=format&fit=crop&w=800&h=600&q=80';
    } else {
      return 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&h=600&q=80';
    }
  };

  // The colors for the gradient - used in MiniAppDetails but needed here
  const colors = {
    gradientStart: '#f59e0b',
    gradientEnd: '#f97316'
  };

  return (
    <div 
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={(e) => e.target === modalRef.current && handleClose()}
    >
      <div 
        ref={contentRef} 
        className="bg-gray-900/95 rounded-xl border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
              }}
            >
              <div style={{ color: colors.gradientStart }}>
                {getIcon()}
              </div>
            </div>
            <h3 className="text-xl font-semibold text-white">{title}</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {getAppDescription()}
              </p>
              
              <h4 className="text-white font-medium mb-3">Key Features:</h4>
              <ul className="space-y-2 text-gray-300">
                {getAppFeatures().map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-indigo-500/20 flex items-center justify-center mt-0.5 shrink-0">
                      <Check className="w-3 h-3 text-indigo-400" />
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white/5 rounded-xl overflow-hidden">
              <img 
                src={getAppImage()} 
                alt={`${title} preview`}
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-white/10 flex justify-end">
          <button
            onClick={handleAddToChatbot}
            className="bg-gradient-to-r from-primary-500 to-purple-600 hover:from-primary-600 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-medium shadow-lg transform transition-all duration-200 hover:scale-105"
          >
            ADD TO MY CHATBOT
          </button>
        </div>
      </div>
    </div>
  );
}

// Mini app card with animations and hover effects
const MiniAppCard = ({ 
  icon, 
  title, 
  description, 
  features, 
  onClick, 
  gradientFrom, 
  gradientTo,
  index
}: {
  icon: React.ReactNode,
  title: string,
  description: string,
  features: { icon: React.ReactNode, text: string }[],
  onClick: () => void,
  gradientFrom: string,
  gradientTo: string,
  index: number
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      }
    );
    
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);
  
  return (
    <div 
      ref={cardRef}
      onClick={onClick}
      className={`
        bg-black/40 backdrop-blur-xl rounded-2xl p-8 border border-white/10 
        hover:border-white/20 transition-all cursor-pointer group overflow-hidden
        relative opacity-0 translate-y-10 duration-500 ease-out
      `}
      style={{ 
        animationDelay: `${index * 100}ms`,
        transitionDelay: `${index * 50}ms`
      }}
    >
      {/* Background gradient blob */}
      <div className="absolute -inset-1 blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-700 ease-in-out" 
        style={{background: `radial-gradient(circle at top right, ${gradientFrom}, ${gradientTo}, transparent 70%)`}}></div>
      
      {/* Top highlight border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{backgroundImage: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})`}}></div>
      
      <div className="relative z-10">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3"
          style={{background: `linear-gradient(135deg, ${gradientFrom}20, ${gradientTo}20)`}}>
          <div className="text-transparent bg-clip-text" 
            style={{backgroundImage: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})`}}>
            {icon}
          </div>
        </div>
        
        <h3 className="text-xl font-bold text-white mb-3 group-hover:translate-x-1 transition-transform">{title}</h3>
        <p className="text-gray-400 mb-6 leading-relaxed">{description}</p>
        
        <div className="space-y-3">
          {features.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2 text-gray-300 group-hover:translate-x-1 transition-transform"
              style={{ transitionDelay: `${idx * 50}ms` }}>
              <div className="text-transparent bg-clip-text" 
                style={{backgroundImage: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})`}}>
                {feature.icon}
              </div>
              <span>{feature.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function MiniApps() {
  const navigate = useNavigate();
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const handleOpenDemo = (demoId: string) => {
    setActiveDemo(demoId);
  };

  const handleCloseDemo = () => {
    setActiveDemo(null);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('animate-in');
              }, index * 100);
            });
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      }
    );
    
    if (heroRef.current) {
      observer.observe(heroRef.current);
    }
    
    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  const getDemoTitle = () => {
    switch (activeDemo) {
      case 'products':
        return 'Product Catalog';
      case 'calendar':
        return 'Appointment Scheduling';
      case 'services':
        return 'Service Presentation';
      case 'videos':
        return 'Video Gallery';
      case 'gallery':
        return 'Photo Gallery';
      case 'realestate':
        return 'Real Estate Properties';
      case 'social':
        return 'Social Media Links';
      case 'order-tracking':
        return 'Order Tracking';
      default:
        return '';
    }
  };

  // Definition of all mini apps
  const miniApps = [
    {
      id: 'products',
      title: 'Product Catalog',
      description: 'Showcase your products interactively with images, descriptions, and prices. Easy integration with Shopify and PrestaShop.',
      icon: <Package2 className="w-7 h-7" />,
      features: [
        { icon: <Sparkles className="w-4 h-4" />, text: 'E-commerce synchronization' },
        { icon: <Zap className="w-4 h-4" />, text: 'Instant search' },
        { icon: <Users className="w-4 h-4" />, text: 'AI recommendations' }
      ],
      gradientFrom: '#6366f1',
      gradientTo: '#8b5cf6'
    },
    {
      id: 'calendar',
      title: 'Appointment Scheduling',
      description: 'Allow your customers to schedule appointments directly from the chat with seamless Calendly integration.',
      icon: <Calendar className="w-7 h-7" />,
      features: [
        { icon: <Sparkles className="w-4 h-4" />, text: 'Calendar synchronization' },
        { icon: <Zap className="w-4 h-4" />, text: 'Automatic confirmation' },
        { icon: <Users className="w-4 h-4" />, text: 'Smart reminders' }
      ],
      gradientFrom: '#8b5cf6',
      gradientTo: '#d946ef'
    },
    {
      id: 'services',
      title: 'Service Presentation',
      description: 'Showcase your services with a visual and interactive presentation, including descriptions and pricing.',
      icon: <ShoppingBag className="w-7 h-7" />,
      features: [
        { icon: <Sparkles className="w-4 h-4" />, text: 'Dynamic carousel' },
        { icon: <Zap className="w-4 h-4" />, text: 'Call to action' },
        { icon: <Users className="w-4 h-4" />, text: 'Instant quotes' }
      ],
      gradientFrom: '#d946ef',
      gradientTo: '#ec4899'
    },
    {
      id: 'videos',
      title: 'Video Gallery',
      description: 'Share your YouTube, Vimeo, or custom videos directly in the chat for an immersive experience.',
      icon: <Video className="w-7 h-7" />,
      features: [
        { icon: <Sparkles className="w-4 h-4" />, text: 'Multi-platforms' },
        { icon: <Zap className="w-4 h-4" />, text: 'Integrated playback' },
        { icon: <Users className="w-4 h-4" />, text: 'Personalized playlists' }
      ],
      gradientFrom: '#ec4899',
      gradientTo: '#f43f5e'
    },
    {
      id: 'gallery',
      title: 'Photo Gallery',
      description: 'Create interactive photo galleries to showcase your achievements, products, or events.',
      icon: <Image className="w-7 h-7" />,
      features: [
        { icon: <Sparkles className="w-4 h-4" />, text: 'Adaptive layouts' },
        { icon: <Zap className="w-4 h-4" />, text: 'Full screen mode' },
        { icon: <Users className="w-4 h-4" />, text: 'Photo albums' }
      ],
      gradientFrom: '#f43f5e',
      gradientTo: '#ef4444'
    },
    {
      id: 'realestate',
      title: 'Real Estate Properties',
      description: 'Present your real estate properties with a dedicated interface including photos, floor plans, and detailed information.',
      icon: <Home className="w-7 h-7" />,
      features: [
        { icon: <Sparkles className="w-4 h-4" />, text: 'Advanced filters' },
        { icon: <Zap className="w-4 h-4" />, text: 'Intelligent display' },
        { icon: <Users className="w-4 h-4" />, text: 'ROI Estimation' }
      ],
      gradientFrom: '#ef4444',
      gradientTo: '#f97316'
    },
    {
      id: 'social',
      title: 'Social Media Links',
      description: 'Connect with your audience across platforms with easy access to all your social media profiles.',
      icon: <Share2 className="w-7 h-7" />,
      features: [
        { icon: <Sparkles className="w-4 h-4" />, text: 'Integration with major platforms' },
        { icon: <Zap className="w-4 h-4" />, text: 'One-click access' },
        { icon: <Users className="w-4 h-4" />, text: 'Customizable icons' }
      ],
      gradientFrom: '#f97316',
      gradientTo: '#eab308'
    },
    {
      id: 'order-tracking',
      title: 'Order Tracking',
      description: 'Allow customers to track their orders directly from the chat interface with real-time status updates.',
      icon: <MessageSquare className="w-7 h-7" />,
      features: [
        { icon: <Sparkles className="w-4 h-4" />, text: 'Real-time updates' },
        { icon: <Zap className="w-4 h-4" />, text: 'Order history lookup' },
        { icon: <Users className="w-4 h-4" />, text: 'Status notifications' }
      ],
      gradientFrom: '#06b6d4',
      gradientTo: '#0ea5e9'
    }
  ];

  return (
    <div className="bg-black min-h-screen">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,58,237,0.15),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(45,212,191,0.15),transparent_70%)]" />
      <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-gradient-to-r from-violet-500/10 to-transparent blur-3xl" />
      <div className="absolute top-40 right-20 w-80 h-80 rounded-full bg-gradient-to-r from-cyan-500/10 to-transparent blur-3xl" />
      <div className="absolute bottom-20 left-1/3 w-72 h-72 rounded-full bg-gradient-to-r from-indigo-500/10 to-transparent blur-3xl" />
      
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-[0.03]" />

      {/* Hero Section */}
      <div ref={heroRef} className="relative py-32 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80 animate-on-scroll opacity-0 translate-y-10">
              <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
              Interactive Experience
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight animate-on-scroll opacity-0 translate-y-10">
              Transform your chatbot with <br />
              <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-500">Mini Apps</GradientText>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed animate-on-scroll opacity-0 translate-y-10">
              Discover our integrated mini-applications that turn your chatbot into an interactive and multi-functional assistant that engages users and drives conversions.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 animate-on-scroll opacity-0 translate-y-10">
              <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-xl hover:shadow-lg hover:shadow-violet-600/20 transition-all transform hover:-translate-y-1 font-medium">
                Explore Plans <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="https://calendly.com/your-company-name/demo" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl hover:bg-white/10 hover:border-violet-500/30 text-white transition-all font-medium">
                Book a Demo
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* What are Mini Apps Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              What are <GradientText className="bg-gradient-to-r from-violet-500 to-cyan-500">Mini Apps?</GradientText>
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              Mini Apps are interactive modules that enhance your chatbot with specific functionalities. 
              They provide a rich and engaging user experience directly within the chat interface, without 
              requiring users to leave the conversation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            <div className="bg-black/40 backdrop-blur-xl rounded-xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-5">
                <Zap className="w-6 h-6 text-violet-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Interactive Experience</h3>
              <p className="text-gray-400">Engage users with dynamic elements that go beyond simple text responses, increasing user satisfaction.</p>
            </div>
            
            <div className="bg-black/40 backdrop-blur-xl rounded-xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-5">
                <Rocket className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Increased Conversion</h3>
              <p className="text-gray-400">Transform your chatbot into a conversion tool that guides users through your sales funnel with targeted interactions.</p>
            </div>
            
            <div className="bg-black/40 backdrop-blur-xl rounded-xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-5">
                <LineChart className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Valuable Insights</h3>
              <p className="text-gray-400">Gather user data and preferences through interactions with mini apps to refine your marketing strategy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mini Apps Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Explore Our <GradientText className="bg-gradient-to-r from-violet-500 to-cyan-500">Mini Apps</GradientText>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Click on any mini app to see a live demo and discover how it can enhance your chatbot experience.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {miniApps.map((app, index) => (
            <MiniAppCard
              key={app.id}
              icon={app.icon}
              title={app.title}
              description={app.description}
              features={app.features}
              onClick={() => handleOpenDemo(app.id)}
              gradientFrom={app.gradientFrom}
              gradientTo={app.gradientTo}
              index={index}
            />
          ))}
        </div>
      </div>

      {/* Call-to-action Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-black/40 backdrop-blur-xl rounded-2xl p-12 border border-white/10 overflow-hidden">
            {/* Background gradient blob */}
            <div className="absolute -inset-1 blur-3xl opacity-20" 
              style={{background: `radial-gradient(circle at bottom right, #8b5cf6, #06b6d4, transparent 70%)`}}></div>
            
            <div className="relative z-10 text-center">
              <h2 className="text-3xl font-bold text-white mb-6">Ready to Enhance Your Chatbot?</h2>
              <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                Add these powerful mini apps to your chatbot and transform it into a powerful conversion tool that engages users and drives results.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-xl hover:shadow-lg hover:shadow-violet-600/20 transition-all transform hover:-translate-y-1 font-medium">
                  Get Started Now <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="px-6 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl hover:bg-white/10 hover:border-violet-500/30 text-white transition-all font-medium">
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Modal */}
      <DemoModal
        isOpen={activeDemo !== null}
        onClose={handleCloseDemo}
        title={getDemoTitle()}
      />

      <style>{`
        .animate-in {
          animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}